import { jsx } from "panel";
import { ButtonEl } from "common/components/button";
import { ButtonSize, ButtonTheme } from "common/components/button/constants";
import { InputEl } from "common/components/input";
import { CalendarEl } from "common/components/calendar";
import { BannerEl } from "common/components/banner";
import { BannerType } from "common/components/banner/types";
import { QueryEntrySectionEl } from "common/query-builder/query-entry-section";
import { RowLabelType, SelectorTypeDefinitions } from "common/query-builder/query-entry-section/types";
import { TemplateType } from "common/report/dashboards/types";
import { SvgIconEl } from "common/components/svg-icon";
import { TileEl, TileTheme } from "common/components/tile";
import { ProgressBarEl } from "common/components/progress-bar";
import { SelectorType } from "common/query-builder/query-block/constants";
import { SelectEl } from "common/components/select";
import {
  BenchmarkKPITemplateFields,
  ECommerceTemplateFields,
  KPITemplateFields,
  LifecycleCohortAnalysisTemplateFields,
  MarketingKPITemplateFields,
  ProductLaunchTemplateFields,
  RHSInputType,
  RHSPanelMode,
  WebAnalyticsTemplateFields
} from "./types";
import { EventAndListPropertySelector } from "./components/EventAndListPropertySelector";
import { FieldLabel } from "./components/FieldLabel";
const commonAttrs = {
  "disable-add-event-filters": true,
  "disable-clone-entry": true,
  "disable-delete-entry": false,
  "disable-first-time-event-filters": true,
  "row-limit": 1,
  "row-label-type": RowLabelType.None,
  "selector-type": SelectorType.Button,
  theme: ButtonTheme.TemplatesEvent,
  "style-override": `.mp-query-entry-section.for-new-ui .mp-query-entry-section-footer, .mp-query-entry-section.for-new-ui .mp-query-entry{width:100%; margin-left: 0px} :host(:not(.contained-section)) .mp-query-entry-section.for-new-ui .mp-query-add-entry-row mp-button:not(.is-open){opacity: 0.7;}`
};
export const PAGE_LOADED_TOOLTIP = `This is how Page Views are tracked on your website.`;
export const PAGE_NAME_TOOLTIP = `This is how Page Names are tracked on your website.`;
export function template() {
  const preselectedEventsSectionFactory = (eventName, tooltipText = null, infoText = null) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row.short-field"
    }, FieldLabel(eventName, tooltipText), infoText && /* @__PURE__ */ jsx("div", {
      sel: ".info-text"
    }, infoText), /* @__PURE__ */ jsx(QueryEntrySectionEl, {
      attrs: {
        ...commonAttrs,
        "add-entry-button-label": `Add Event`,
        "project-id": parseInt(this.getContext(`currentProject`).id, 10),
        "track-name": `RHS`,
        selected: this.state.fields[eventName]
      },
      on: {
        update: (ev) => {
          this.helpers.handleEventUpdate(ev, eventName);
        }
      }
    }));
  };
  const propertiesSelector = (propertyKey, tooltipText, infoText) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row.short-field"
    }, FieldLabel(propertyKey, tooltipText), infoText && /* @__PURE__ */ jsx("div", {
      sel: ".info-text"
    }, infoText), /* @__PURE__ */ jsx(QueryEntrySectionEl, {
      attrs: {
        ...commonAttrs,
        "disable-add-entry-button-tooltip-text": true,
        "disable-map-to-lookup-table": true,
        "disable-typecast": true,
        "add-entry-button-label": `Add Property`,
        "project-id": parseInt(this.getContext(`currentProject`).id, 10),
        "row-types": [SelectorTypeDefinitions.Segment],
        type: `properties`,
        selected: this.state.fields[propertyKey]
      },
      on: {
        update: (ev) => {
          if (ev.detail.entries.length) {
            this.helpers.updateField(ev.detail.updatedEntry.event.propertyName, propertyKey, RHSInputType.Property);
          } else {
            this.helpers.removeField(propertyKey);
          }
        }
      }
    }));
  };
  const templateCalendarInputField = (fieldName) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row.short-field"
    }, FieldLabel(fieldName), /* @__PURE__ */ jsx(ButtonEl, {
      attrs: {
        icon: `calendar`,
        theme: ButtonTheme.TemplatesEvent,
        pressed: this.state.calendarInputOpen,
        elref: `calendarButton`
      },
      on: { click: () => this.update({ calendarInputOpen: true }) }
    }, this.state.fields[fieldName] || `Select date`), this.state.calendarInputOpen && /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx(CalendarEl, {
      attrs: {
        "is-double-calendar": false,
        "is-range": false,
        "is-from-date-focused": true
      },
      on: {
        selectDate: (ev) => {
          this.helpers.updateField(ev.detail.date, fieldName, RHSInputType.Date);
          this.update({ calendarInputOpen: false });
        }
      }
    })));
  };
  const templateInputField = (fieldName, placeholder) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row"
    }, FieldLabel(fieldName), /* @__PURE__ */ jsx(InputEl, {
      attrs: { placeholder },
      on: { input: (ev) => this.helpers.updateField(ev.target.value, fieldName, RHSInputType.Text) }
    }));
  };
  const kpiTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      KPITemplateFields.SIGN_UP,
      `This is the event where a new user\u2014free or paid\u2014is acquired by creating a new account. Your event might be called \u201CAccount created\u201D instead of \u201CSign up.\u201D Don't have this type of event? Try using \u201CSession start\u201D or a similar event. [Learn More](${this.WHAT_TO_TRACK_URL})`
    ), preselectedEventsSectionFactory(KPITemplateFields.VALUE_MOMENT, this.VALUE_MOMENT_FIELD_TOOLTIP_TEXT));
  };
  const productLaunchTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, templateInputField(ProductLaunchTemplateFields.FEATURE_NAME, `Ex. Reactions on Comments`), templateCalendarInputField(`Launch Date`), preselectedEventsSectionFactory(
      ProductLaunchTemplateFields.NEW_FEATURE,
      `This is the event that captures the adoption of your new feature. For adding reviews to a streaming service, the event could be publishing a review.`
    ), preselectedEventsSectionFactory(
      ProductLaunchTemplateFields.FEATURE_VALUE_MOMENT,
      this.VALUE_MOMENT_FIELD_TOOLTIP_TEXT
    ));
  };
  const lifecycleCohortAnalysisTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      LifecycleCohortAnalysisTemplateFields.VALUE_MOMENT,
      this.VALUE_MOMENT_FIELD_TOOLTIP_TEXT
    ));
  };
  const marketingKpiTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      MarketingKPITemplateFields.ACTIVATION_EVENT,
      `This is the event that marks a user as "active" for you. This could be viewing a webpage or entering an email address, or opening an app.`,
      `Will default to "Any Event" if none selected`
    ), preselectedEventsSectionFactory(
      MarketingKPITemplateFields.CONVERSION_EVENT,
      `This is the desired user behavior. For a social media platform, this could be creating a post, for e-commerce it might be completing a purchase.`
    ));
  };
  const eCommerceTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, EventAndListPropertySelector(
      this,
      ECommerceTemplateFields.PURCHASE_EVENT,
      ECommerceTemplateFields.CART_PROPERTY,
      ECommerceTemplateFields.PRICE_KEY,
      `Purchase, Cart, and Price`,
      false,
      `Adding a purchase event, cart property, and price property helps us uncover user purchase behaviors. [Learn More](${this.WHAT_TO_TRACK_URL})`,
      `Purchase`
    ), EventAndListPropertySelector(
      this,
      ECommerceTemplateFields.ADD_TO_CART_EVENT,
      ECommerceTemplateFields.CART_PROPERTY_2,
      ECommerceTemplateFields.CATEGORY_KEY,
      `Add to Cart, Cart, and Category`,
      !this.state.fields[ECommerceTemplateFields.PRICE_KEY],
      `Adding an Add to Cart event, cart property, and cateogry property helps us uncover user purchase behaviors. [Learn More](${this.WHAT_TO_TRACK_URL})`,
      `Add to Cart`
    ), preselectedEventsSectionFactory(
      ECommerceTemplateFields.PRODUCT_VIEWED_EVENT,
      `The Product Viewed Event helps us see users behavior across the entire purchasing funnel. [Learn More](${this.WHAT_TO_TRACK_URL})`
    ));
  };
  const webAnalyticsTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      WebAnalyticsTemplateFields.PAGE_LOADED_EVENT,
      PAGE_LOADED_TOOLTIP,
      `What do you consider a page view?`
    ), propertiesSelector(
      WebAnalyticsTemplateFields.PAGE_NAME_PROPERTY,
      PAGE_NAME_TOOLTIP,
      `How do you identify the name of a page?`
    ));
  };
  const benchmarkIndustrySelector = () => {
    var _a;
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-mapping-row.short-field"
    }, FieldLabel(`Industry`, null, `Optional`), /* @__PURE__ */ jsx(SelectEl, {
      sel: ".benchmark-industry-selector",
      attrs: {
        "menu-height": 500,
        "selected-option": (_a = this.state.fields[BenchmarkKPITemplateFields.INDUSTRY]) != null ? _a : `Select Industry`,
        sections: [
          {
            items: [
              `Other`,
              `Technology`,
              `Media & Entertainment`,
              `Financial Services`,
              `Retailing`,
              `Healthcare`,
              `Gaming`
            ].map((item) => ({
              label: item,
              value: item
            }))
          }
        ]
      },
      on: {
        change: (ev) => {
          this.helpers.updateField(ev.detail.value, BenchmarkKPITemplateFields.INDUSTRY, RHSInputType.Text);
        }
      }
    }));
  };
  const benchmarkKpiTemplateFields = () => {
    return /* @__PURE__ */ jsx(null, null, preselectedEventsSectionFactory(
      BenchmarkKPITemplateFields.SIGN_UP,
      `This is the event where a new user\u2014free or paid\u2014is acquired by creating a new account. Your event might be called \u201CAccount created\u201D instead of \u201CSign up.\u201D Don't have this type of event? Try using \u201CSession start\u201D or a similar event. [Learn More](${this.WHAT_TO_TRACK_URL})`
    ), preselectedEventsSectionFactory(BenchmarkKPITemplateFields.VALUE_MOMENT, this.VALUE_MOMENT_FIELD_TOOLTIP_TEXT), benchmarkIndustrySelector());
  };
  const templateFields = () => {
    switch (this.params.selectedTemplate) {
      case TemplateType.KPI:
        return kpiTemplateFields();
      case TemplateType.FEATURE_LAUNCH:
        return productLaunchTemplateFields();
      case TemplateType.LIFECYCLE_COHORT_ANALYSIS:
        return lifecycleCohortAnalysisTemplateFields();
      case TemplateType.MARKETING_KPI:
        return marketingKpiTemplateFields();
      case TemplateType.E_COMMERCE:
        return eCommerceTemplateFields();
      case TemplateType.WEB_ANALYTICS:
        return webAnalyticsTemplateFields();
      case TemplateType.BENCHMARK_KPI:
        return benchmarkKpiTemplateFields();
      default:
        return null;
    }
  };
  const progressBar = (length) => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".progress-bar-container"
    }, /* @__PURE__ */ jsx("div", {
      sel: ".current-step"
    }, this.state.tutorialCurrentStepIndex + 1 === length ? `Completed` : `Step ${this.state.tutorialCurrentStepIndex + 1} of ${length - 1}`), /* @__PURE__ */ jsx(ProgressBarEl, {
      sel: ".progress-bar-meter",
      attrs: { percent: (this.state.tutorialCurrentStepIndex + 1) / length * 100 }
    }));
  };
  const infoTile = () => {
    var _a, _b;
    const { showTutorial } = this.state;
    const tutorialConfig = this.helpers.getTemplateTutorialConfig();
    return /* @__PURE__ */ jsx(TileEl, {
      sel: ".info-tile",
      params: {
        theme: TileTheme.NO_IMAGE,
        fillWidth: true,
        ...!showTutorial && {
          title: `${(_a = this.TEMPLATE_CONFIGS[this.params.selectedTemplate]) == null ? void 0 : _a.title} Template`,
          description: (_b = this.TEMPLATE_CONFIGS[this.params.selectedTemplate]) == null ? void 0 : _b.rhsDescription
        }
      }
    }, tutorialConfig ? /* @__PURE__ */ jsx(null, null, showTutorial ? /* @__PURE__ */ jsx("div", null, Object.values(tutorialConfig)[this.state.tutorialCurrentStepIndex].stepInfo, progressBar(Object.values(tutorialConfig).length)) : /* @__PURE__ */ jsx("div", {
      sel: ".tutorial-button-container"
    }, /* @__PURE__ */ jsx(ButtonEl, {
      attrs: { theme: ButtonTheme.Primary, size: ButtonSize.Small },
      on: { click: this.helpers.startTutorial }
    }, "Show me how"), /* @__PURE__ */ jsx(ButtonEl, {
      attrs: { size: ButtonSize.Small },
      on: { click: this.helpers.dismissTutorial }
    }, "Not now"))) : /* @__PURE__ */ jsx("a", {
      sel: ".mp-font-weight-bold",
      attrs: { href: this.WHAT_TO_TRACK_URL, target: `_blank` }
    }, "Learn more"));
  };
  const eventSelectStep = () => {
    return /* @__PURE__ */ jsx("div", {
      sel: ".event-select-step"
    }, !this.state.hideTutorial && infoTile(), !this.helpers.hasIntegratedArb() && /* @__PURE__ */ jsx(BannerEl, {
      attrs: {
        "body-text": `Track ${this.helpers.getNoDataEventString()} to get started with this template. [Learn More](${this.WHAT_TO_TRACK_URL})`,
        type: BannerType.Embedded
      }
    }), templateFields());
  };
  return /* @__PURE__ */ jsx("div", {
    sel: ".right-panel"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".container"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".header"
  }, "Set Up Board"), /* @__PURE__ */ jsx("div", {
    class: { "event-select-height": this.params.mode === RHSPanelMode.Templates },
    sel: ".body"
  }, this.params.mode === RHSPanelMode.StarterBoard && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".event-description"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".description-title"
  }, "All Events"), /* @__PURE__ */ jsx("div", {
    sel: ".description-body"
  }, "This board will automatically begin showing your data once events are sent to Mixpanel")), /* @__PURE__ */ jsx("div", {
    sel: ".event-item"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".icon"
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: `custom-event-beta` }
  })), /* @__PURE__ */ jsx("div", {
    sel: ".event-name"
  }, "All Events"))), this.params.mode === RHSPanelMode.Templates && /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", null, eventSelectStep()), /* @__PURE__ */ jsx(ButtonEl, {
    sel: ".submit-btn",
    attrs: {
      theme: ButtonTheme.Primary,
      disabled: this.helpers.shouldDisableFinishButton(),
      elref: `createTemplateButton`,
      icon: this.params.isLoading ? `spinner` : ``
    },
    on: {
      click: this.helpers.createTemplateBoard
    }
  }, "Finish")))));
}
